/**
 * Fixed responsive shells for tablet and mobile (ADR-UI-001 decision 5).
 * Tablet uses kit Tabs. Mobile deliberately renders one focused operational
 * view at a time so nested desktop/tabbed modules do not become one enormous
 * page and do not download or mount work the user has not selected.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { ScreenHead } from "../ui/screen-head";
import { Tabs } from "../ui/tabs";
import type { PanelContentRegistry, WorkspacePanelContent } from "./content";
import type { PanelDescriptor } from "./types";
import type { WorkspaceStore } from "./workspace-store";

export interface StackedWorkspaceProps {
  readonly store: WorkspaceStore;
  readonly contentRegistry?: PanelContentRegistry | undefined;
  readonly mode: "tablet" | "mobile";
  /** Route/deep-link requested panel. Ignored when the id is not in this layout. */
  readonly requestedPanel?: string | null;
  /** Mobile only: whether to repeat the selected panel title above its body. */
  readonly showPanelHead?: boolean | undefined;
  /**
   * Mobile only: the panels that get an always-visible icon chip above the
   * full panel select. Ids not present in the layout are skipped. Icons are
   * supplied by the caller so this shell stays free of app-level imports.
   */
  readonly quickPanels?: readonly QuickPanelDescriptor[] | undefined;
}

export interface QuickPanelDescriptor {
  readonly id: string;
  readonly icon: ReactNode;
}

function panelTitle(
  store: WorkspaceStore,
  panel: PanelDescriptor,
  content: WorkspacePanelContent | undefined,
): ReactNode {
  return content?.title ?? `${store.text("panelRegion")} · ${panel.id}`;
}

function panelLabel(
  panel: PanelDescriptor,
  content: WorkspacePanelContent | undefined,
): string {
  return content?.label ??
    (typeof content?.title === "string" ? content.title : panel.id);
}

function renderBody(
  store: WorkspaceStore,
  content: WorkspacePanelContent | undefined,
): ReactNode {
  if (content === undefined) {
    return <p className="thw-panel__empty">{store.text("emptyContent")}</p>;
  }
  return content.render();
}

const TabletShell = observer(function TabletShell({
  contentRegistry,
  requestedPanel = null,
  store,
}: Omit<StackedWorkspaceProps, "mode">): ReactNode {
  const panels = store.panels;
  const [activeId, setActiveId] = useState<string>(
    panels.some((panel) => panel.id === requestedPanel)
      ? requestedPanel ?? ""
      : panels[0]?.id ?? "",
  );
  useEffect(() => {
    if (
      requestedPanel !== null &&
      panels.some((panel) => panel.id === requestedPanel)
    ) setActiveId(requestedPanel);
  }, [panels, requestedPanel]);
  const active = panels.some((panel) => panel.id === activeId)
    ? activeId
    : panels[0]?.id ?? "";
  const activeContent = contentRegistry?.get(active);

  return (
    <div className="thw-workspace thw-workspace--tablet" data-device-mode="tablet">
      <Tabs
        activeId={active}
        label={store.text("panelTablist")}
        onChange={setActiveId}
        tabs={panels.map((panel) => ({
          id: panel.id,
          label: panelTitle(store, panel, contentRegistry?.get(panel.id)),
        }))}
      >
        {renderBody(store, activeContent)}
      </Tabs>
    </div>
  );
});

const MobileShell = observer(function MobileShell({
  contentRegistry,
  quickPanels,
  requestedPanel = null,
  showPanelHead = true,
  store,
}: Omit<StackedWorkspaceProps, "mode">): ReactNode {
  const panels = store.panels;
  const [activeId, setActiveId] = useState<string>(
    panels.some((panel) => panel.id === requestedPanel)
      ? requestedPanel ?? ""
      : panels[0]?.id ?? "",
  );
  useEffect(() => {
    if (
      requestedPanel !== null &&
      panels.some((panel) => panel.id === requestedPanel)
    ) setActiveId(requestedPanel);
  }, [panels, requestedPanel]);
  const activePanel =
    panels.find((panel) => panel.id === activeId) ??
    panels[0];
  const activeContent =
    activePanel === undefined ? undefined : contentRegistry?.get(activePanel.id);

  const visibleQuickPanels = (quickPanels ?? []).filter((quick) =>
    panels.some((panel) => panel.id === quick.id));

  return (
    <div className="thw-workspace thw-workspace--mobile" data-device-mode="mobile">
      {/* One sticky control block: the quick chips must never scroll away
          underneath the (equally sticky) exhaustive panel picker. */}
      <div className="thw-mobile-workspace__controls">
      {visibleQuickPanels.length === 0 ? null : (
        <nav aria-label={store.text("quickPanels")} className="thw-mobile-workspace__quick">
          {visibleQuickPanels.map((quick) => {
            const panel = panels.find((candidate) => candidate.id === quick.id);
            const content = contentRegistry?.get(quick.id);
            const label = content?.shortLabel ??
              (panel === undefined ? quick.id : panelLabel(panel, content));
            return (
              <button
                aria-pressed={activePanel?.id === quick.id}
                key={quick.id}
                onClick={() => { setActiveId(quick.id); }}
                type="button"
              >
                <span aria-hidden="true" className="thw-mobile-workspace__quick-icon">
                  {quick.icon}
                </span>
                <span className="thw-mobile-workspace__quick-label">{label}</span>
              </button>
            );
          })}
        </nav>
      )}
      <label className="thw-mobile-workspace__picker">
        <span>{store.text("panelTablist")}</span>
        <select
          onChange={(event) => {
            setActiveId(event.target.value);
          }}
          value={activePanel?.id ?? ""}
        >
          {panels.map((panel) => {
            const content = contentRegistry?.get(panel.id);
            return (
              <option key={panel.id} value={panel.id}>
                {panelLabel(panel, content)}
              </option>
            );
          })}
        </select>
      </label>
      </div>
      {activePanel === undefined ? (
        <p className="thw-panel__empty">{store.text("emptyContent")}</p>
      ) : (
        <section
          aria-label={panelLabel(activePanel, activeContent)}
          className="thw-stack__item thw-mobile-workspace__active"
        >
          {showPanelHead ? (
            <ScreenHead
              className="thw-stack__header"
              headingLevel={3}
              title={panelTitle(store, activePanel, activeContent)}
            />
          ) : null}
          <div className="thw-stack__body">
            {renderBody(store, activeContent)}
          </div>
        </section>
      )}
    </div>
  );
});

export const StackedWorkspace = observer(function StackedWorkspace({
  contentRegistry,
  mode,
  quickPanels,
  requestedPanel = null,
  showPanelHead = true,
  store,
}: StackedWorkspaceProps): ReactNode {
  return mode === "tablet" ? (
    <TabletShell contentRegistry={contentRegistry} requestedPanel={requestedPanel} store={store} />
  ) : (
    <MobileShell
      contentRegistry={contentRegistry}
      quickPanels={quickPanels}
      requestedPanel={requestedPanel}
      showPanelHead={showPanelHead}
      store={store}
    />
  );
});
